import {
  TRAFFIC_LIGHT_LABELS,
  targetPeriodLabel,
  type ObjectivePanel,
  type TrafficLight,
} from '@fluxus/shared';
import { STATUS_DOT } from '@/components/status/StatusBadge';
import { cn } from '@/lib/cn';

/** Desempenho do objetivo: rótulos e a tendência do ano (mapa estratégico e detalhe do objetivo — DEC-052). */

export const formatValue = (v: number) => v.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
export const withUnit = (v: number, unit: string) =>
  unit === 'R$' ? `R$ ${formatValue(v)}` : `${formatValue(v)} ${unit}`;
const percent = (v: number | null) => (v === null ? null : `${formatValue(v)}%`);
export const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Rótulo do semáforo com a performance (ou o estado, quando não há número). */
export const lightLabel = (performance: number | null, light: TrafficLight) =>
  percent(performance) ?? TRAFFIC_LIGHT_LABELS[light];

/** Tendência do ano: uma barra por mês, na cor do semáforo (altura até 150% de performance). */
export function Trend({
  trend,
  month,
  year,
}: {
  trend: ObjectivePanel['trend'];
  month: number;
  year: number;
}) {
  const byMonth = new Map(trend.map((t) => [t.month, t]));
  return (
    <section aria-label={`Tendência de ${year}`}>
      <h3 className="text-sm font-semibold">Tendência de {year}</h3>
      <ol className="mt-2 grid h-28 grid-cols-12 items-end gap-1">
        {MONTHS.map((m) => {
          const t = byMonth.get(m);
          const light = t?.light ?? 'gray';
          const height = t?.performance != null ? Math.max(4, Math.min(t.performance, 150) / 1.5) : 4;
          const label = targetPeriodLabel('MONTH', m);
          return (
            <li
              key={m}
              className="flex h-full flex-col items-center justify-end gap-1"
              aria-label={`${label}: ${t ? lightLabel(t.performance, light) : 'sem cálculo'}`}
              aria-current={m === month ? 'true' : undefined}
            >
              <span
                className={cn(
                  'w-full rounded-sm',
                  STATUS_DOT[light],
                  m === month && 'ring-2 ring-foreground/60',
                )}
                style={{ height: `${height}%` }}
                aria-hidden
              />
              <span
                className={cn(
                  'text-[10px] text-muted-foreground',
                  m === month && 'font-semibold text-foreground',
                )}
                aria-hidden
              >
                {label.slice(0, 3)}
              </span>
            </li>
          );
        })}
      </ol>
    </section>
  );
}
